/*
This file is part of the Notesnook project (https://notesnook.com/)

Copyright (C) 2023 Streetwriters (Private) Limited

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU General Public License for more details.

You should have received a copy of the GNU General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/** Chrome/Edge noise from ResizeObserver; must not crash the app. */
export function isBenignResizeObserverError(message: string | undefined) {
  if (!message) return false;
  const normalized = message.toLowerCase();
  return normalized.includes("resizeobserver loop");
}

/** Fires when the page reloads while SQLite SharedWorker requests are in flight. */
export function isSharedServiceClosedError(message: string | undefined) {
  if (!message) return false;
  return message.includes("SharedService closed");
}

export function getWindowErrorMessage(
  event: ErrorEvent | PromiseRejectionEvent
): string {
  if ("reason" in event) {
    const reason = event.reason;
    if (reason instanceof Error) return reason.message;
    return String(reason ?? "");
  }
  const errorEvent = event as ErrorEvent;
  let message = String(errorEvent.message || errorEvent.error?.message || "");
  const target = errorEvent.target;
  if (
    !message &&
    target &&
    "src" in target &&
    typeof (target as HTMLScriptElement).src === "string"
  ) {
    const src = (target as HTMLScriptElement).src;
    if (src) message = `Failed to fetch dynamically imported module: ${src}`;
  }
  return message;
}

export function suppressBenignWindowError(
  event: ErrorEvent | PromiseRejectionEvent
) {
  const message = getWindowErrorMessage(event);
  if (
    isBenignResizeObserverError(message) ||
    isSharedServiceClosedError(message)
  ) {
    event.preventDefault();
    if ("stopImmediatePropagation" in event) {
      event.stopImmediatePropagation();
    }
    return true;
  }
  return false;
}
